import { z } from 'zod';

export interface SpecialSeatTypeOverlayProps {
  onCancel: () => void;
  onConfirm: () => void;
  seatClickedMessage: SeatClickedMessage;
}

export interface SeatDetailsOverlayProps extends SpecialSeatTypeOverlayProps {}

export const specialSeatTypesEnum = [
  'ADA',
  'ADA_COMPANION',
  'LIMITED_VIEW',
  'OBSTRUCTED_VIEW',
  'STANDING_ROOM'
] as const;

// Shared schemas
const invalidMessageSchema = z.object({
  message: z.string(),
  type: z.literal('INVALID_MESSAGE')
});

// Parent to SYOS schemas
const provideConfigurationMessageSchema = z.object({
  currentPerformanceId: z.union([z.string(), z.number()]),
  isTouch: z.boolean(),
  performances: z.array(
    z.object({
      date: z.number(),
      id: z.union([z.string(), z.number()]),
      label: z.string().optional(),
      prices: z.array(
        z.object({
          priceTypeId: z.union([z.string(), z.number()]),
          price: z.string(),
          zoneId: z.union([z.string(), z.number()])
        })
      ),
      zoneMapId: z.union([z.string(), z.number()])
    })
  ),
  photoPreviewsSeatIds: z.array(z.union([z.string(), z.number()])).optional(),
  priceTypes: z.array(
    z.object({
      id: z.union([z.string(), z.number()]),
      order: z.number()
    })
  ),
  requestId: z.string(),
  type: z.literal('PROVIDE_CONFIGURATION')
});

const provideSeatStatusesMessageSchema = z.object({
  requestId: z.string(),
  seatStatuses: z.record(z.union([z.string(), z.number()]), z.boolean()),
  type: z.literal('PROVIDE_SEAT_STATUSES')
});

const setI18nMessageSchema = z.object({
  backToOverview: z.string().optional(),
  dateAndTime: z.string().optional(),
  enablePhotoPreviews: z.string().optional(),
  errorConfigurationLoad: z.string().optional(),
  photoPreviews: z.string().optional(),
  photoPreviewsHelp: z.string().optional(),
  priceTypes: z.record(z.union([z.string(), z.number()]), z.string()).optional(),
  section: z.string().optional(),
  sections: z.record(z.string(), z.string()).optional(),
  type: z.literal('SET_I18N'),
  zones: z.record(z.union([z.string(), z.number()]), z.string()).optional()
});

const setSelectedSeatsMessageSchema = z.object({
  seatIds: z.array(z.union([z.string(), z.number()])),
  type: z.literal('SET_SELECTED_SEATS')
});

// SYOS to parent schemas
const requestConfigurationMessageSchema = z.object({
  requestId: z.string(),
  type: z.literal('REQUEST_CONFIGURATION')
});

const seatClickedMessageSchema = z.object({
  isSelected: z.boolean(),
  seat: z.object({
    id: z.union([z.string(), z.number()]),
    seatNumber: z.string(),
    seatRow: z.string(),
    x: z.number(),
    y: z.number()
  }),
  seatType: z.object({
    id: z.union([z.string(), z.number()]),
    specialHandling: z.enum(specialSeatTypesEnum).nullable()
  }),
  section: z.object({
    label: z.string(),
    name: z.string()
  }),
  type: z.literal('SEAT_CLICKED'),
  zone: z.object({
    id: z.union([z.string(), z.number()]).nullable(),
    label: z.string().nullable()
  })
});

const requestSeatStatusesMessageSchema = z.object({
  requestId: z.string(),
  zoneIds: z.array(z.union([z.string(), z.number()])),
  type: z.literal('REQUEST_SEAT_STATUSES')
});

const photoPreviewClickedMessageSchema = z.object({
  seatId: z.union([z.string(), z.number()]),
  type: z.literal('PHOTO_PREVIEW_CLICKED')
});

// Grouped schemas
const parentToSyosMessageSchema = z.discriminatedUnion('type', [
  invalidMessageSchema,
  provideConfigurationMessageSchema,
  provideSeatStatusesMessageSchema,
  // provideZoneMapMessageSchema,
  setI18nMessageSchema,
  setSelectedSeatsMessageSchema
]);

export const syosToParentMessageSchema = z.discriminatedUnion('type', [
  invalidMessageSchema,
  photoPreviewClickedMessageSchema,
  requestConfigurationMessageSchema,
  requestSeatStatusesMessageSchema,
  seatClickedMessageSchema
]);

export const wrappedMessageSchema = z.object({
  payload: z.unknown(),
  source: z.literal('jixaw-syos')
});

export type RequestSeatStatusesMessage = z.infer<typeof requestSeatStatusesMessageSchema>;
// type InvalidMessage = z.infer<typeof invalidMessageSchema>;
export type ParentToSyosMessage = z.infer<typeof parentToSyosMessageSchema>;
export type PhotoPreviewClickedMessage = z.infer<typeof photoPreviewClickedMessageSchema>;
export type ProvideConfigurationMessage = z.infer<typeof provideConfigurationMessageSchema>;
export type ProvideSeatStatusesMessage = z.infer<typeof provideSeatStatusesMessageSchema>;
// type ProvideZoneMapMessage = z.infer<typeof provideZoneMapMessageSchema>;
export type SetI18nMessage = z.infer<typeof setI18nMessageSchema>;
export type RequestConfigurationMessage = z.infer<typeof requestConfigurationMessageSchema>;
export type SeatClickedMessage = z.infer<typeof seatClickedMessageSchema>;
export type SetSelectedSeatsMessage = z.infer<typeof setSelectedSeatsMessageSchema>;
export type SyosToParentMessage = z.infer<typeof syosToParentMessageSchema>;
// type WrappedMessage = z.infer<typeof wrappedMessageSchema>;

export type MessageHandlers = Record<SyosToParentMessage['type'], (message: SyosToParentMessage) => void>;

// export interface InvalidMessageDTO {
//   message: string;
//   type: 'INVALID_MESSAGE';
// }

// export interface ProvideConfigurationMessageDTO {
//   currentPerformanceId: string | number;
//   isTouch: boolean;
//   performances: Array<{
//     date: number;
//     id: string | number;
//     label?: string;
//     prices: Array<{
//       priceTypeId: string | number;
//       price: string;
//       zoneId: string | number;
//     }>;
//     zoneMapId: string | number;
//   }>;
//   photoPreviewsSeatIds?: Array<string | number>;
//   priceTypes: Array<{
//     id: string | number;
//     order: number;
//   }>;
//   requestId: string;
//   type: 'PROVIDE_CONFIGURATION';
// }

// export interface ProvideSeatStatusesMessageDTO {
//   requestId: string;
//   seatStatuses: Record<string | number, boolean>;
//   type: 'PROVIDE_SEAT_STATUSES';
// }

// export interface SetI18nMessageDTO {
//   backToOverview?: string;
//   dateAndTime?: string;
//   enablePhotoPreviews?: string;
//   errorConfigurationLoad?: string;
//   photoPreview?: string;
//   photoPreviewsHelp?: string;
//   priceTypes?: Record<string | number, string>;
//   section?: string;
//   sections?: Record<string, string>;
//   type: 'SET_I18N';
//   zones?: Record<string | number, string>;
// }

// export interface SetSelectedSeatsMessageDTO {
//   seatIds: Array<string | number>;
//   type: 'SET_SELECTED_SEATS';
// }

// export interface RequestConfigurationMessageDTO {
//   requestId: string;
//   type: 'REQUEST_CONFIGURATION';
// }

// type SeatTypeSpecialHandling = 'ADA' | 'ADA_COMPANION' | 'LIMITED_VIEW' | 'OBSTRUCTED_VIEW' | 'STANDING_ROOM';

// export interface SeatClickedMessageDTO {
//   isSelected: boolean;
//   seat: {
//     id: string | number;
//     seatNumber: string;
//     seatRow: string;
//     x: number;
//     y: number;
//   };
//   seatType: {
//     id: string | number;
//     specialHandling: SeatTypeSpecialHandling | null;
//   };
//   section: {
//     label: string;
//     name: string;
//   },
//   type: 'SEAT_CLICKED';
//   zone: {
//     id: string | number | null;
//     label: string | null;
//   }
// };

// export interface RequestSeatStatusesMessageDTO {
//   requestId: string;
//   zoneIds: Array<string | number>;
//   type: 'REQUEST_SEAT_STATUSES';
// }

// export interface PhotoPreviewClickedMessageDTO {
//   seatId: string | number;
//   type: 'PHOTO_PREVIEW_CLICKED';
// }
